import { useEffect, useState, type ReactNode } from "react";

import { listeZuText, splitListe, type StoffEigenschaft } from "@repo/shared";

import { useDialog } from "./Bestaetigen";
import { AuswahlListe } from "./MehrfachAuswahl";
import { Popover } from "./Popover";

/* --- Symbole: schlichte Linien (Pfade nach Lucide, ISC-Lizenz) ---------------------------- */

const PFADE = {
  archiv: (
    <>
      <rect width="20" height="5" x="2" y="3" rx="1" />
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
      <path d="M10 12h4" />
    </>
  ),
  flamme: (
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  ),
  recycling: (
    <>
      <path d="M7 19H4.815a1.83 1.83 0 0 1-1.57-.881 1.785 1.785 0 0 1-.004-1.784L7.196 9.5" />
      <path d="M11 19h8.203a1.83 1.83 0 0 0 1.556-.89 1.784 1.784 0 0 0 0-1.775l-1.226-2.12" />
      <path d="m14 16-3 3 3 3" />
      <path d="M8.293 13.596 7.196 9.5 3.1 10.598" />
      <path d="m9.344 5.811 1.093-1.892A1.83 1.83 0 0 1 11.985 3a1.784 1.784 0 0 1 1.546.888l3.943 6.843" />
      <path d="m13.378 9.633 4.096 1.098 1.097-4.096" />
    </>
  ),
  notiz: (
    <>
      <path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8Z" />
      <path d="M15 3v4a2 2 0 0 0 2 2h4" />
    </>
  ),
  datei: (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </>
  ),
  hoch: (
    <>
      <path d="m5 12 7-7 7 7" />
      <path d="M12 19V5" />
    </>
  ),
  runter: (
    <>
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </>
  ),
  kreuz: (
    <>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </>
  ),
} as const;

export type ZeichenName = keyof typeof PFADE;

/** Inline-SVG; mit `titel` als Bild mit Tooltip, sonst nur Schmuck. */
export function Zeichen({ name, titel, className }: { name: ZeichenName; titel?: string; className?: string }) {
  return (
    <svg
      className={className ? `stoffe-symbol ${className}` : "stoffe-symbol"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={titel ? "img" : undefined}
      aria-label={titel}
      aria-hidden={titel ? undefined : true}
    >
      {titel && <title>{titel}</title>}
      {PFADE[name]}
    </svg>
  );
}

/* --- Formate ----------------------------------------------------------------------------- */

const EURO = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export const euroText = (cents: number) => EURO.format(cents / 100);

export const faktorText = (faktor: number) =>
  faktor.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 3 });

/** „07.10.2026“ – wie im ERP mit führender Null. */
export function datumText(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function Leer() {
  return <span className="muted">—</span>;
}

export const EIGENSCHAFTEN: { wert: StoffEigenschaft; label: string; symbol: ZeichenName }[] = [
  { wert: "schwerEntflammbar", label: "Schwer entflammbar", symbol: "flamme" },
  { wert: "recyclebar", label: "Recyclebar", symbol: "recycling" },
];

/* --- Anzeige ----------------------------------------------------------------------------- */

export function Badges({ werte }: { werte: string[] }) {
  if (werte.length === 0) return <Leer />;
  return (
    <span className="stoffe-badges">
      {werte.map((w) => (
        <span key={w} className="badge stoffe-badge">
          {w}
        </span>
      ))}
    </span>
  );
}

type Eigenschaften = Record<StoffEigenschaft, boolean>;

export function EigenschaftenSymbole({ werte }: { werte: Eigenschaften }) {
  const gesetzt = EIGENSCHAFTEN.filter((e) => werte[e.wert]);
  if (gesetzt.length === 0) return <Leer />;
  return (
    <span className="stoffe-symbole">
      {gesetzt.map((e) => (
        <Zeichen key={e.wert} name={e.symbol} titel={e.label} className={`stoffe-symbol--${e.symbol}`} />
      ))}
    </span>
  );
}

/* --- Bearbeiten in der Tabelle ----------------------------------------------------------- */

const gleicheWerte = (a: string[], b: string[]) =>
  [...a].map((w) => w.toLowerCase()).sort().join("\n") === [...b].map((w) => w.toLowerCase()).sort().join("\n");

/** Knopf in der Zelle: sieht aus wie der Inhalt, öffnet den Editor. */
function ZellenKnopf({ label, offen, children }: { label: string; offen: boolean; children: ReactNode }) {
  return (
    <button type="button" className="stoffe-zelle-knopf" aria-haspopup="dialog" aria-expanded={offen} aria-label={label}>
      {children}
    </button>
  );
}

/**
 * Typ- bzw. Material-Zelle: Klick öffnet die Häkchen-Liste. Gespeichert wird beim Schließen,
 * nur wenn sich etwas geändert hat. Bis die Liste neu geladen ist, zeigt die Zelle den neuen Stand.
 */
export function WerteZelle({
  wert,
  bezeichnung,
  optionen,
  neuerWert,
  onSpeichern,
}: {
  wert: string | null;
  bezeichnung: string;
  optionen: string[];
  neuerWert: (name: string) => Promise<string | null>;
  onSpeichern(text: string | null): Promise<boolean>;
}) {
  const werte = splitListe(wert);
  const [offen, setOffen] = useState(false);
  const [entwurf, setEntwurf] = useState<string[]>([]);
  const [ausstehend, setAusstehend] = useState<string[] | null>(null);

  useEffect(() => setAusstehend(null), [wert]);

  async function umschalten(neu: boolean) {
    if (neu === offen) return;
    setOffen(neu);
    if (neu) {
      setEntwurf(ausstehend ?? werte);
      return;
    }
    if (gleicheWerte(entwurf, ausstehend ?? werte)) return;
    setAusstehend(entwurf);
    if (!(await onSpeichern(listeZuText(entwurf)))) setAusstehend(null);
  }

  const sichtbar = offen ? entwurf : (ausstehend ?? werte);
  return (
    <Popover
      offen={offen}
      onOffen={(o) => void umschalten(o)}
      label={`${bezeichnung} bearbeiten`}
      className="stoffe-popover__inhalt--auswahl"
      ausloeser={
        <ZellenKnopf offen={offen} label={`${bezeichnung} bearbeiten: ${sichtbar.join(", ") || "leer"}`}>
          <Badges werte={sichtbar} />
        </ZellenKnopf>
      }
    >
      <AuswahlListe optionen={optionen} gewaehlt={entwurf} onChange={setEntwurf} neuerWert={neuerWert} />
    </Popover>
  );
}

/** Eigenschaften-Zelle: zwei Häkchen im Popover, gespeichert beim Schließen. */
export function EigenschaftenZelle({
  werte,
  onSpeichern,
}: {
  werte: Eigenschaften;
  onSpeichern(werte: Eigenschaften): Promise<boolean>;
}) {
  const [offen, setOffen] = useState(false);
  const [entwurf, setEntwurf] = useState<Eigenschaften>(werte);
  const [ausstehend, setAusstehend] = useState<Eigenschaften | null>(null);
  const { schwerEntflammbar, recyclebar } = werte;

  useEffect(() => setAusstehend(null), [schwerEntflammbar, recyclebar]);

  async function umschalten(neu: boolean) {
    if (neu === offen) return;
    setOffen(neu);
    const stand = ausstehend ?? werte;
    if (neu) {
      setEntwurf(stand);
      return;
    }
    if (EIGENSCHAFTEN.every((e) => entwurf[e.wert] === stand[e.wert])) return;
    setAusstehend(entwurf);
    if (!(await onSpeichern(entwurf))) setAusstehend(null);
  }

  const sichtbar = offen ? entwurf : (ausstehend ?? werte);
  const text = EIGENSCHAFTEN.filter((e) => sichtbar[e.wert]).map((e) => e.label).join(", ");
  return (
    <Popover
      offen={offen}
      onOffen={(o) => void umschalten(o)}
      label="Eigenschaften bearbeiten"
      className="stoffe-popover__inhalt--auswahl"
      ausloeser={
        <ZellenKnopf offen={offen} label={`Eigenschaften bearbeiten: ${text || "keine"}`}>
          <EigenschaftenSymbole werte={sichtbar} />
        </ZellenKnopf>
      }
    >
      <div className="stoffe-auswahl-liste__werte">
        {EIGENSCHAFTEN.map((e) => (
          <label key={e.wert} className="stoffe-auswahl-liste__wert">
            <input
              type="checkbox"
              checked={entwurf[e.wert]}
              onChange={(ev) => setEntwurf((alt) => ({ ...alt, [e.wert]: ev.target.checked }))}
            />
            {e.label}
          </label>
        ))}
      </div>
    </Popover>
  );
}

/* --- Foto und Notiz ---------------------------------------------------------------------- */

function istPdf(url: string): boolean {
  try {
    return new URL(url, window.location.href).pathname.toLowerCase().endsWith(".pdf");
  } catch {
    return url.toLowerCase().endsWith(".pdf");
  }
}

/** Vorschau 32 px; Klick zeigt das Bild groß im Dialog. Ein PDF öffnet im neuen Tab. */
export function FotoZelle({ url, name }: { url: string | null; name: string }) {
  const [offen, setOffen] = useState(false);
  const ref = useDialog(offen);

  if (!url) return <Leer />;
  if (istPdf(url)) {
    return (
      <a className="stoffe-pdf" href={url} target="_blank" rel="noreferrer">
        <Zeichen name="datei" />
        PDF öffnen
      </a>
    );
  }
  return (
    <>
      <button type="button" className="stoffe-foto" onClick={() => setOffen(true)} aria-label={`Foto von „${name}“ vergrößern`}>
        <img src={url} alt="" width={32} height={32} loading="lazy" />
      </button>
      <dialog
        ref={ref}
        className="stoffe-dialog stoffe-dialog--bild"
        aria-label={`Foto von „${name}“`}
        onClose={() => setOffen(false)}
        onClick={(e) => e.target === e.currentTarget && setOffen(false)}
      >
        {offen && (
          <div className="stoffe-dialog__inhalt">
            <div className="stoffe-dialog__kopf">
              <h2>{name}</h2>
              <button type="button" className="stoffe-symbolknopf" onClick={() => setOffen(false)} aria-label="Schließen" title="Schließen">
                <Zeichen name="kreuz" />
              </button>
            </div>
            <img src={url} alt={name} />
          </div>
        )}
      </dialog>
    </>
  );
}

/** Notiz-Symbol mit Popover – nur, wenn eine Notiz gesetzt ist. */
export function NotizZelle({ notiz }: { notiz: string | null }) {
  const [offen, setOffen] = useState(false);
  if (!notiz?.trim()) return <Leer />;
  return (
    <Popover
      offen={offen}
      onOffen={setOffen}
      label="Notiz"
      className="stoffe-popover__inhalt--notiz"
      ausloeser={
        <button type="button" className="stoffe-symbolknopf" title="Notiz" aria-label="Notiz anzeigen" aria-expanded={offen}>
          <Zeichen name="notiz" />
        </button>
      }
    >
      <p className="stoffe-notiz">{notiz}</p>
    </Popover>
  );
}
